import { useState, useContext, useCallback, useEffect } from 'react';

import { FormControlLabel, Switch } from '@mui/material';
import { HuntingGroupFields } from '@ragu-components/ord-types';
import { useTranslate, TextField } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import ResourceList from '../../components/ResourceList';
import { personalEndpointContext } from '../../context/personalEndpointContext';
import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps } from '../../dataProvider/types';
import type { Identifier } from 'react-admin';

export interface HuntType extends Record<string, unknown> {
  enableScreening: boolean;
  parallel: boolean;
  huntingGroupId?: Identifier | null;
}

interface HuntControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: HuntType;
}

export default function HuntControls({ updateNodeData, data, record }: HuntControlsProps): React.ReactElement {
  const [enableScreening, setEnableScreening] = useState<boolean>(data.enableScreening ?? false);
  const [parallel, setParallel] = useState<boolean>(data.parallel ?? false);
  const [huntingGroupId, setHuntingGroupId] = useState<Identifier | null>(
    data.huntingGroupId !== undefined && data.huntingGroupId !== '' ? data.huntingGroupId : null
  );
  const translate = useTranslate();
  const isPersonal = useContext(personalEndpointContext);
  const huntResourceName = isPersonal ? `me/hunting_groups` : 'hunting_groups';

  // If the flowchart has an owner, it means it is a mobility received from the global mobilities endpoint:
  const hasOwner = record !== undefined && 'owner' in record && record.owner !== null && record.owner !== undefined;
  const onHuntDataUpdate = useCallback((): void => {
    flowchartLogger.debug('HuntControls: onHuntDataUpdate', { enableScreening, parallel, huntingGroupId });
    if (parallel === null) {
      flowchartLogger.debug('HuntControls: number is null. Update cancelled.');
      return;
    }
    updateNodeData({
      enableScreening,
      parallel,
      huntingGroupId,
    });
  }, [enableScreening, parallel, huntingGroupId, updateNodeData]);

  useEffect(() => {
    flowchartLogger.debug('HuntControls: huntingGroupId changed', { huntingGroupId });
    onHuntDataUpdate();
  }, [huntingGroupId, onHuntDataUpdate]);

  return (
    <NodeControlForm
      onSubmit={(event) => {
        event.preventDefault();
        onHuntDataUpdate();
      }}
      data={data}
      updateNodeData={updateNodeData}
    >
      <FormControlLabel
        control={
          <Switch
            checked={enableScreening}
            onChange={() => {
              setEnableScreening(!enableScreening);
            }}
          />
        }
        label={translate('callflows.nodes.hunt.enableScreening')}
      />
      <FormControlLabel
        control={
          <Switch
            checked={parallel}
            onChange={() => {
              setParallel(!parallel);
            }}
          />
        }
        label={translate('callflows.nodes.hunt.parallel')}
      />
      <ResourceList
        resource={huntResourceName}
        selectedResourceId={huntingGroupId}
        filter={
          hasOwner && record !== undefined
            ? { active: 'fiql', fiql: `${HuntingGroupFields['owner.id']}==${record.owner.id as string}` }
            : undefined
        }
        onRowClick={(id) => {
          setHuntingGroupId(id);
          return false;
        }}
      >
        <TextField source={HuntingGroupFields.name} />
        <TextField source={HuntingGroupFields.description} />
      </ResourceList>
    </NodeControlForm>
  );
}
